/** @format */

import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useFilteredNavigation } from "./RoleBasedNavigation";
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  UserGroupIcon,
  TagIcon,
  StarIcon,
  RectangleStackIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  ChevronRightIcon,
  ClockIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  PlayIcon,
  MegaphoneIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";

// Organized navigation groups with enhanced metadata
const navigationGroups = [
  {
    title: "Main",
    items: [
      {
        name: "Dashboard",
        icon: HomeIcon,
        path: "/",
        description: "Overview & analytics",
      },
    ],
  },
  {
    title: "Profile",
    items: [
      {
        name: "My Profile",
        icon: UserIcon,
        path: "/profile",
        description: "Account settings",
      },
      {
        name: "Settings",
        icon: Cog6ToothIcon,
        path: "/settings",
        description: "Preferences & configuration",
      },
    ],
  },
  {
    title: "Content Management",
    items: [
      {
        name: "Blogs",
        icon: DocumentTextIcon,
        path: "/collection/blogs",
        description: "Manage blog posts",
      },
      {
        name: "Courses",
        icon: AcademicCapIcon,
        path: "/collection/courses",
        description: "Course catalog",
      },
      {
        name: "Books",
        icon: BookOpenIcon,
        path: "/collection/books",
        description: "Book library management",
      },
    ],
  },
  {
    title: "User Management",
    items: [
      {
        name: "Users",
        icon: UserGroupIcon,
        path: "/collection/users",
        hasSubcollection: true,
        description: "User database",
      },
      {
        name: "Enrolled Students",
        icon: UserPlusIcon,
        path: "/enrolled-students",
        description: "Course enrollments",
      },
      {
        name: "Pending Users",
        icon: ClockIcon,
        path: "/pending-users",
        description: "Approval queue",
      },
      {
        name: "Book Reviews",
        icon: StarIcon,
        path: "/collection/BooksReviews",
        description: "Book ratings",
      },
      {
        name: "Withdrawals",
        icon: CurrencyDollarIcon,
        path: "/withdrawals",
        description: "Manage withdrawal requests",
      },
      {
        name: "Payments",
        icon: CurrencyDollarIcon,
        path: "/payments",
        description: "Manage payment requests",
      },
    ],
  },
  {
    title: "Communication",
    items: [
      {
        name: "Announcements",
        icon: MegaphoneIcon,
        path: "/announcements",
        description: "System announcements",
        adminOnly: true,
      },
    ],
  },

  {
    title: "System",
    items: [
      {
        name: "Categories",
        icon: RectangleStackIcon,
        path: "/collection/categories",
        description: "Content organization",
      },
      {
        name: "Tags",
        icon: TagIcon,
        path: "/collection/tags",
        description: "Content tagging",
      },
    ],
  },
  {
    title: "Learning",
    items: [
      {
        name: "Classrooms",
        icon: AcademicCapIcon,
        path: "/classrooms",
        hasSubcollection: true,
        description: "Virtual learning",
      },
      {
        name: "Free Resources",
        icon: PlayIcon,
        path: "/free-resources",
        description: "Educational videos",
      },
    ],
  },
];

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { currentUser, logout, userRole } = useAuth();

  // Get filtered navigation based on user role
  const filteredNavigationGroups = useFilteredNavigation(navigationGroups);

  const isActiveRoute = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const getActiveStyles = (isActive) => {
    return isActive
      ? "bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30 border-l-4 border-l-white/80 transform scale-[1.02]"
      : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-primary-50 hover:text-primary-700 hover:border-l-4 hover:border-l-primary-300 hover:shadow-md hover:shadow-primary-500/10 hover:transform hover:scale-[1.01]";
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 ">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-all duration-300 ${
          sidebarOpen ? "block" : "hidden"
        }`}
      >
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md"
          onClick={() => setSidebarOpen(false)}
        ></div>
        <div className="relative flex w-full max-w-xs h-full flex-col bg-white/95 backdrop-blur-xl shadow-2xl border-r border-gray-200/50 overflow-hidden mobile-sidebar-container">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/40 hover:bg-white/30 transition-all duration-200"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto pb-4 mobile-sidebar-scroll flex flex-col">
            {/* Logo Section */}
            <div className="flex items-center justify-center py-4 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border-b border-blue-700 shadow-xl">
              <div className="flex flex-col items-center justify-center">
                <div className="relative mb-3">
                  <div className="absolute inset-0 bg-blue-400 rounded-full blur-lg opacity-30"></div>
                  <img
                    src="/images/icon.png"
                    alt="ProfStudy Icon"
                    className="relative h-16 w-16 object-cover rounded-full border-2 border-blue-300 shadow-xl"
                  />
                </div>
                <div className="text-center">
                  <h1 className="text-lg font-bold text-white mb-1">
                    Admin Panel
                  </h1>
                  <p className="text-sm text-blue-200 font-medium tracking-wide">
                    ProfStudy Management
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="mt-6 px-4 space-y-4 flex-1">
              {filteredNavigationGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="space-y-2">
                  <h3 className="px-3 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gradient-to-r from-gray-100 to-gray-50 rounded-lg">
                    {group.title}
                  </h3>
                  <div className="space-y-1.5">
                    {group.items.map((item) => {
                      const isActive = isActiveRoute(item.path);
                      return (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 ease-out ${getActiveStyles(
                            isActive
                          )}`}
                        >
                          <div
                            className={`p-1.5 rounded-lg transition-all duration-300 ${
                              isActive
                                ? "bg-white/20"
                                : "bg-gray-100 group-hover:bg-primary-100"
                            }`}
                          >
                            <item.icon
                              className={`h-5 w-5 transition-all duration-300 ${
                                isActive
                                  ? "text-white"
                                  : "text-gray-600 group-hover:text-primary-600"
                              }`}
                            />
                          </div>
                          <div className="ml-3 flex-1">
                            <span className="block font-semibold">
                              {item.name}
                            </span>
                            {item.description && (
                              <span
                                className={`block text-xs mt-0.5 transition-all duration-300 ${
                                  isActive
                                    ? "text-white/80"
                                    : "text-gray-500 group-hover:text-primary-600"
                                }`}
                              >
                                {item.description}
                              </span>
                            )}
                          </div>
                          {item.hasSubcollection && (
                            <ChevronRightIcon
                              className={`h-4 w-4 transition-all duration-300 ${
                                isActive
                                  ? "text-white/80"
                                  : "text-gray-400 group-hover:text-primary-500"
                              }`}
                            />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* User Menu - Mobile */}
            <div className="mt-auto px-4 pb-4 flex-shrink-0">
              <div className="bg-gradient-to-br from-primary-50 via-white to-primary-50 rounded-2xl p-4 border border-primary-200/50 shadow-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white">
                      <UserCircleIcon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {currentUser?.email || "Admin User"}
                    </p>
                    <p className="text-xs text-primary-700 bg-primary-100 text-primary-800 px-3 py-1 rounded-full inline-block mt-1 font-medium">
                      {userRole
                        ? userRole.charAt(0).toUpperCase() + userRole.slice(1)
                        : "User"}
                    </p>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  <Link
                    to="/profile"
                    className="flex items-center px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-white hover:text-primary-700 rounded-xl transition-all duration-200 hover:shadow-md"
                  >
                    <UserIcon className="mr-3 h-4 w-4" />
                    Profile
                  </Link>
                  <button
                    onClick={logout}
                    className="w-full flex items-center px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-white hover:text-red-600 rounded-xl transition-all duration-200 hover:shadow-md"
                  >
                    <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-80 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-1 flex-col min-h-0 bg-white/95 backdrop-blur-xl border-r border-gray-200/50 shadow-2xl">
          <div className="flex flex-1 flex-col  pb-6 overflow-y-auto">
            {/* Logo Section */}
            <div className="flex sticky top-0 z-50 items-center justify-center py-6 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border-b border-blue-700 shadow-xl">
              <div className="flex flex-col items-center">
                <div className="relative mb-4">
                  <div className="absolute inset-0 bg-blue-400 rounded-full blur-lg opacity-30"></div>
                  <img
                    src="/images/icon.png"
                    alt="ProfStudy Icon"
                    className="relative h-24 w-24 object-contain rounded-full border-2 border-blue-300 shadow-xl"
                  />
                </div>
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-white mb-2">
                    Admin Panel
                  </h1>
                  <p className="text-base text-blue-200 font-medium tracking-wide">
                    ProfStudy Management
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="mt-10 flex-1 px-6 space-y-8">
              {filteredNavigationGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="space-y-4">
                  <h3 className="px-4 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl border border-gray-200/50">
                    {group.title}
                  </h3>
                  <div className="space-y-2">
                    {group.items.map((item) => {
                      const isActive = isActiveRoute(item.path);
                      return (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`group flex items-center px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-300 ease-out ${getActiveStyles(
                            isActive
                          )}`}
                        >
                          <div
                            className={`p-2.5 rounded-xl transition-all duration-300 ${
                              isActive
                                ? "bg-white/20"
                                : "bg-gray-100 group-hover:bg-primary-100"
                            }`}
                          >
                            <item.icon
                              className={`h-5 w-5 transition-all duration-300 ${
                                isActive
                                  ? "text-white"
                                  : "text-gray-600 group-hover:text-primary-600"
                              }`}
                            />
                          </div>
                          <div className="ml-4 flex-1">
                            <span className="block font-semibold">
                              {item.name}
                            </span>
                            {item.description && (
                              <span
                                className={`block text-xs mt-1 transition-all duration-300 ${
                                  isActive
                                    ? "text-white/80"
                                    : "text-gray-500 group-hover:text-primary-600"
                                }`}
                              >
                                {item.description}
                              </span>
                            )}
                          </div>
                          {item.hasSubcollection && (
                            <ChevronRightIcon
                              className={`h-4 w-4 transition-all duration-300 ${
                                isActive
                                  ? "text-white/80"
                                  : "text-gray-400 group-hover:text-primary-500"
                              }`}
                            />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* User Menu - Desktop */}
            <div className="flex-shrink-0 px-6 pb-6">
              <div className="bg-gradient-to-br from-primary-50 via-white to-primary-50 rounded-2xl p-6 border border-primary-200/50 shadow-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white">
                      <UserCircleIcon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {currentUser?.email || "Admin User"}
                    </p>
                    <p className="text-xs text-primary-700 bg-primary-100 text-primary-800 px-3 py-1 rounded-full inline-block mt-1 font-medium">
                      {userRole
                        ? userRole.charAt(0).toUpperCase() + userRole.slice(1)
                        : "User"}
                    </p>
                  </div>
                  <div className="ml-3 flex space-x-2">
                    <Link
                      to="/profile"
                      className="p-2.5 text-gray-400 hover:text-primary-600 hover:bg-white hover:shadow-md rounded-xl transition-all duration-200"
                      title="Profile"
                    >
                      <UserIcon className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={logout}
                      className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-white hover:shadow-md rounded-xl transition-all duration-200"
                      title="Sign Out"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-80 flex flex-col flex-1 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="sticky top-0 z-10 lg:hidden pl-3 pt-3 sm:pl-3 sm:pt-3 bg-transparent">
          <button
            type="button"
            className="group relative -ml-0.5 -mt-0.5 h-14 w-14 inline-flex items-center justify-center rounded-2xl text-gray-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-300 ease-out bg-white/90 backdrop-blur-md border border-gray-200/50 shadow-lg hover:shadow-2xl hover:scale-105 hover:bg-white hover:border-primary-200"
            onClick={() => setSidebarOpen(true)}
          >
            <div className="relative">
              <Bars3Icon className="h-6 w-6 transition-all duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 bg-primary-500/10 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </button>
        </div>
        <main className="flex-1 min-h-screen">
          <div className="py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
